// Angular
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
// RxJS
import { of, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
// Services and Models
import {
  Doctor,
  DoctorActionTypes,
  DoctorOnServerCreated,
  selectDoctorLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { map, skip } from 'rxjs/operators';
import { OfficeModel, OfficesService } from 'src/app/core/management';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-doctor-add',
  templateUrl: './doctor-add.dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDoctorDialogComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreateDoctorDialogComponent>,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private officeService: OfficesService,
    public dialog: MatDialog,
  ) {}

  doctorForm: FormGroup;
  hasFormErrors: boolean;
  clicked: boolean;
  companyId: number;
  filteredOffices: any[];
  recorderList: OfficeModel[] = [];
  filteredRecorder;
  private subscriptions: Subscription[] = [];
  @ViewChild('recorderInput') recorderInput: ElementRef<HTMLInputElement>;
  @ViewChild('officeAutocompleteComponent')
  officeAutocompleteComponent: GeneralAutocompleteComponent;
  officeAutocompleteCall = this.officeService.searchOffices.bind(this.officeService);
  officeOptionFormatter = (val) => (val && val.name ? val.name : '');

  ngOnInit() {
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res !== undefined ? res.companyID : -1;
    });
    this.subscriptions.push(userSub);
    this.createForm();
    const successSubscription = this.store
      .pipe(select(selectDoctorLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          switch (res.action_type) {
            case DoctorActionTypes.DoctorCreated:
              this.dialogRef.close({ added: true });
              break;
            default:
              break;
          }
        }
      });
    this.subscriptions.push(successSubscription);
  }

  getTitle() {
    return this.translate.instant('DOCTOR.NEW_DOCTOR');
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.doctorForm.controls;
    this.officeAutocompleteComponent.updateValueAndValidity();

    /** check form */
    if (this.doctorForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    this.clicked = true;
    this.createDoctor(this.prepareDoctor());
  }

  /**
   * Returns object for saving
   */
  prepareDoctor(): Doctor {
    const controls = this.doctorForm.controls;
    const _doctor = new Doctor();
    _doctor.clear();
    _doctor.dea = controls.dea.value;
    _doctor.address_1 = controls.address_1.value;
    _doctor.address_2 = controls.address_2.value;
    _doctor.npi = controls.npi.value;
    _doctor.sln = controls.sln.value;
    _doctor.office = controls.office_id.value;
    _doctor.first_name = controls.first_name.value;
    _doctor.last_name = controls.last_name.value;
    _doctor.practice_name = controls.practice_name.value;
    _doctor.email = controls.email.value;
    _doctor.office_phone =
      controls.office_phone.value !== null
        ? controls.office_phone.value.replace(/\D/g, '')
        : controls.office_phone.value;
    _doctor.zip = controls.zip.value;
    _doctor.cell_phone =
      controls.cell_phone.value !== null
        ? controls.cell_phone.value.replace(/\D/g, '')
        : controls.cell_phone.value;
    _doctor.is_active = controls.is_active.value;
    _doctor.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _doctor.city = controls.city.value;
    _doctor.urbanization = controls.urbanization.value;
    _doctor.state = controls.state.value ? controls.state.value.toUpperCase() : '';
    _doctor.doctor_id = controls.doctor_id.value;
    for (const [key, value] of Object.entries(_doctor)) {
      if (typeof value === 'string') {
        _doctor[key] = value.trim();
      }
    }
    return _doctor;
  }

  /**
   * Create doctor
   *
   * @param _doctor: Doctor
   */
  createDoctor(_doctor: Doctor) {
    this.store.dispatch(new DoctorOnServerCreated({ doctor: _doctor }));
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.doctorForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  close() {
    this.dialogRef.close();
  }

  createForm() {
    this.doctorForm = this.fb.group({
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      practice_name: [null],
      email: [null, Validators.compose([Validators.email])],
      city: [null],
      state: [null, Validators.pattern(/^[A-Za-z]{2}$/)],
      address_1: [null],
      address_2: [null],
      is_active: [true],
      zip: [null, [Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]],
      office_phone: [null, [Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)]],
      cell_phone: [null, [Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)]],
      fax: [null, Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)],
      office_id: [null],
      dea: [null],
      npi: [null],
      urbanization: [null],
      sln: [null],
      doctor_id: ['', [Validators.pattern(/^[A-Za-z0-9]+$/)]],
    });
  }

  getStatusString(): string {
    switch (this.doctorForm.get('is_active').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  recorderInputChanged(value) {
    if (value) {
      if (typeof value === 'string')
        this.filteredRecorder = this.officeService.searchOffices(value).pipe(
          map((res) =>
            res.filter((r) => {
              return !this.recorderList.find((e: any) => e.id === r.id);
            }),
          ),
        );
    } else {
      this.filteredRecorder = of([]);
    }
  }

  displayFnRecorder(event: any): string {
    return event && event.name ? event.name : '';
  }

  selectedRecorder(event: MatAutocompleteSelectedEvent): void {
    this.recorderList.push(event.option.value);
    this.recorderInput.nativeElement.value = event.option.value.name;
  }

  onOfficeChange(val) {
    this.doctorForm.controls.office_id.setValue(val);
    this.doctorForm.controls.office_id.updateValueAndValidity();
  }
}
